/**
 * HistorialExportaciones - Acordeón con historial de exportaciones de tesorería
 * Muestra información de exportaciones realizadas y permite descargar archivos
 */
import React, { useState } from 'react';
import {
  Box, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Typography, Button, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { formatearFecha, formatearMonto } from '../../utils/formatters';
import tesoreriaService from '../../services/tesoreriaService';

const HistorialExportaciones = ({
  exportaciones = [],
  loading = false,
  error = null,
  rutEmpresa = ''
}) => {
  const [descargando, setDescargando] = useState(null);
  const [detalleDialogOpen, setDetalleDialogOpen] = useState(false);
  const [exportacionSeleccionada, setExportacionSeleccionada] = useState(null);
  const [errorDescarga, setErrorDescarga] = useState('');

  // Determinar color de estado
  const getColorEstado = (estado) => {
    switch (estado) {
      case 'OK':
        return 'success';
      case 'PENDIENTE':
        return 'info';
      case 'ERROR':
        return 'error';
      default:
        return 'default';
    }
  };

  // Determinar icono de estado
  const getIconoEstado = (estado) => {
    switch (estado) {
      case 'OK':
        return <CheckIcon sx={{ fontSize: 18 }} />;
      case 'PENDIENTE':
        return <CircularProgress size={18} />;
      case 'ERROR':
        return <ErrorIcon sx={{ fontSize: 18 }} />;
      default:
        return null;
    }
  };

  // Descargar archivo de exportación
  const handleDescargar = async (exportacionId, rutaArchivo) => {
    setDescargando(exportacionId);
    setErrorDescarga('');

    try {
      const blob = await tesoreriaService.descargarExportacion(exportacionId);

      // Extraer solo el nombre del archivo de la ruta completa
      const nombreArchivo = rutaArchivo
        ? rutaArchivo.split(/[\/\\]/).pop()
        : `exportacion_${exportacionId}.csv`;

      // Crear link de descarga
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrorDescarga(
        error.response?.data?.detail ||
        error.message ||
        'Error al descargar archivo'
      );
    } finally {
      setDescargando(null);
    }
  };

  // Abrir diálogo de detalles
  const handleVerDetalles = (exportacion) => {
    setExportacionSeleccionada(exportacion);
    setDetalleDialogOpen(true);
  };

  // Cerrar diálogo
  const handleCerrarDetalle = () => {
    setExportacionSeleccionada(null);
    setDetalleDialogOpen(false);
  };

  if (error) {
    return (
      <Accordion defaultExpanded={false} sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Historial de Exportaciones
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="error">{error}</Alert>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <>
      <Accordion defaultExpanded={false} sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
              Historial de Exportaciones
            </Typography>
            {exportaciones.length > 0 && (
              <Chip
                label={`${exportaciones.length} exportaciones`}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
              <CircularProgress size={40} />
            </Box>
          ) : exportaciones.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="textSecondary">
                No hay exportaciones realizadas aún
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha Exportación</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Período</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Folios</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Movimientos</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Valor Total</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Estado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {exportaciones.map((exp) => (
                    <TableRow key={exp.id} sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
                      {/* Fecha Exportación */}
                      <TableCell>
                        {formatearFecha(exp.fecha_exportacion)}
                      </TableCell>

                      {/* Período */}
                      <TableCell>
                        <Chip
                          label={exp.periodo}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>

                      {/* Folios */}
                      <TableCell align="right">
                        {exp.folio_final > 0
                          ? `${exp.folio_inicial} - ${exp.folio_final}`
                          : `${exp.folio_inicial}`}
                      </TableCell>

                      {/* Cantidad Movimientos */}
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {exp.cantidad_movimientos}
                        </Typography>
                      </TableCell>

                      {/* Valor Total */}
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                          {formatearMonto(exp.valor_total)}
                        </Typography>
                      </TableCell>

                      {/* Estado */}
                      <TableCell align="center">
                        <Chip
                          label={exp.estado}
                          color={getColorEstado(exp.estado)}
                          size="small"
                          icon={getIconoEstado(exp.estado)}
                          variant="outlined"
                        />
                      </TableCell>

                      {/* Acciones */}
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {/* Botón de detalles */}
                          <Tooltip title="Ver detalles">
                            <Button
                              size="small"
                              startIcon={<InfoIcon />}
                              onClick={() => handleVerDetalles(exp)}
                              variant="text"
                            />
                          </Tooltip>

                          {/* Botón de descarga (solo si hay archivo) */}
                          {exp.archivo_generado && exp.estado === 'OK' && (
                            <Tooltip title="Descargar archivo">
                              <Button
                                size="small"
                                startIcon={
                                  descargando === exp.id
                                    ? <CircularProgress size={16} />
                                    : <DownloadIcon />
                                }
                                onClick={() => handleDescargar(
                                  exp.id,
                                  exp.archivo_generado
                                )}
                                disabled={descargando === exp.id}
                                variant="text"
                                color="primary"
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Error de descarga */}
          {errorDescarga && (
            <Box sx={{ p: 2, backgroundColor: '#ffebee', borderTop: '1px solid #ffcdd2' }}>
              <Alert severity="error" onClose={() => setErrorDescarga('')}>
                {errorDescarga}
              </Alert>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Diálogo de detalles */}
      {exportacionSeleccionada && (
        <Dialog open={detalleDialogOpen} onClose={handleCerrarDetalle} maxWidth="sm" fullWidth>
          <DialogTitle>
            Detalles de Exportación
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Estado */}
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Estado
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={exportacionSeleccionada.estado}
                    color={getColorEstado(exportacionSeleccionada.estado)}
                    icon={getIconoEstado(exportacionSeleccionada.estado)}
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Período */}
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  Período
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                  {exportacionSeleccionada.periodo}
                </Typography>
              </Box>

              {/* Fecha */}
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  Fecha de Exportación
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                  {formatearFecha(exportacionSeleccionada.fecha_exportacion)}
                </Typography>
              </Box>

              {/* Folios */}
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  Folios
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                  {exportacionSeleccionada.folio_final > 0
                    ? `${exportacionSeleccionada.folio_inicial} a ${exportacionSeleccionada.folio_final}`
                    : `${exportacionSeleccionada.folio_inicial}`}
                </Typography>
              </Box>

              {/* Cantidad de Movimientos */}
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  Cantidad de Movimientos
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                  {exportacionSeleccionada.cantidad_movimientos}
                </Typography>
              </Box>

              {/* Valor Total */}
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  Valor Total
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5, color: '#2e7d32' }}>
                  {formatearMonto(exportacionSeleccionada.valor_total)}
                </Typography>
              </Box>

              {/* Archivo Generado */}
              {exportacionSeleccionada.archivo_generado && (
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Archivo
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5, wordBreak: 'break-word' }}>
                    {exportacionSeleccionada.archivo_generado}
                  </Typography>
                </Box>
              )}

              {/* Mensaje de Error (si aplica) */}
              {exportacionSeleccionada.mensaje_error && (
                <Alert severity="error">
                  {exportacionSeleccionada.mensaje_error}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            {exportacionSeleccionada.archivo_generado && exportacionSeleccionada.estado === 'OK' && (
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => {
                  handleDescargar(
                    exportacionSeleccionada.id,
                    exportacionSeleccionada.archivo_generado
                  );
                  handleCerrarDetalle();
                }}
                variant="contained"
                color="primary"
              >
                Descargar Archivo
              </Button>
            )}
            <Button onClick={handleCerrarDetalle}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default HistorialExportaciones;
